import { useEffect, useRef, type CSSProperties, type KeyboardEvent } from 'react';
import { NavIcon, type NavIconName } from './NavIcon.js';

export interface SidebarItem {
  id: string;
  label: string;
  icon: NavIconName;
  count?: number;
}

export interface SidebarUser {
  initials: string;
  name: string;
  role: string;
}

export interface SidebarProps {
  activeId: string;
  primaryItems: SidebarItem[];
  secondaryItems: SidebarItem[];
  user: SidebarUser;
  brand?: {
    title: string;
    subtitle: string;
    /** When set, the Grow logo image renders instead of the monogram + text. */
    logoSrc?: string;
  };
  onNavigate: (id: string) => void;
  /**
   * 'static' (default) is the fixed 240px column. 'drawer' is the narrow-screen
   * form: off-canvas until `open`, modal while open, closed via `onClose`.
   */
  variant?: 'static' | 'drawer';
  open?: boolean;
  onClose?: () => void;
}

const STATIC_STYLE: CSSProperties = {
  width: 240,
  flexShrink: 0,
  background: 'var(--bg-elev)',
  borderRight: '1px solid var(--gc-line)',
  display: 'flex',
  flexDirection: 'column',
  padding: '24px 18px',
  height: '100vh',
  position: 'sticky',
  top: 0,
};

const DRAWER_MS = 220;

export function Sidebar({
  activeId,
  primaryItems,
  secondaryItems,
  user,
  brand = { title: 'Grow&Co', subtitle: 'Survey' },
  onNavigate,
  variant = 'static',
  open = false,
  onClose,
}: SidebarProps) {
  const isDrawer = variant === 'drawer';
  const asideRef = useRef<HTMLElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);

  // While the drawer is open: focus moves into it, Escape closes it, the page
  // behind doesn't scroll, and focus returns to whatever opened it on close.
  useEffect(() => {
    if (!isDrawer || !open) return;
    const opener = document.activeElement as HTMLElement | null;
    closeRef.current?.focus();
    const onKey = (e: globalThis.KeyboardEvent) => {
      if (e.key === 'Escape') onClose?.();
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
      opener?.focus?.();
    };
  }, [isDrawer, open, onClose]);

  // Keep Tab inside the open drawer (it is modal).
  const trapTab = (e: KeyboardEvent<HTMLElement>) => {
    if (e.key !== 'Tab' || !asideRef.current) return;
    const focusable = asideRef.current.querySelectorAll<HTMLElement>('button, [href]');
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (!first || !last) return;
    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  };

  const reduceMotion =
    typeof window !== 'undefined' &&
    typeof window.matchMedia === 'function' &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const ms = reduceMotion ? 0 : DRAWER_MS;

  const drawerStyle: CSSProperties = {
    ...STATIC_STYLE,
    width: 'min(280px, 85vw)',
    position: 'fixed',
    top: 0,
    bottom: 0,
    left: 0,
    height: 'auto',
    overflowY: 'auto',
    zIndex: 1000,
    boxShadow: open ? '0 10px 40px rgba(28, 42, 34, 0.18)' : 'none',
    transform: open ? 'translateX(0)' : 'translateX(-100%)',
    // Hidden (so not focusable) only once the slide-out has finished.
    visibility: open ? 'visible' : 'hidden',
    transition: `transform ${ms}ms var(--ease-out, ease-out), visibility 0s linear ${open ? 0 : ms}ms`,
  };

  const aside = (
    <aside
      ref={asideRef}
      style={isDrawer ? drawerStyle : STATIC_STYLE}
      {...(isDrawer
        ? {
            role: 'dialog',
            'aria-modal': true,
            'aria-label': 'Main menu',
            'aria-hidden': !open,
            onKeyDown: trapTab,
          }
        : {})}
    >
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '4px 8px 22px',
          borderBottom: '1px solid #F0F2EE',
          marginBottom: 18,
        }}
      >
        {brand.logoSrc ? (
          <img
            src={brand.logoSrc}
            alt={brand.title}
            style={{ width: 150, height: 'auto', objectFit: 'contain', mixBlendMode: 'multiply' }}
          />
        ) : (
          <>
            <div
              aria-hidden="true"
              style={{
                width: 44,
                height: 44,
                borderRadius: 10,
                background: 'var(--gc-green-soft)',
                color: 'var(--gc-green-dark)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontFamily: 'var(--font-sans)',
                fontWeight: 600,
                fontSize: 18,
              }}
            >
              G&amp;
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.1 }}>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 600,
                  fontSize: 14,
                  color: 'var(--fg)',
                }}
              >
                {brand.title}
              </span>
              <span
                style={{
                  fontFamily: 'var(--font-sans)',
                  fontWeight: 400,
                  fontSize: 10,
                  color: 'var(--gc-stone)',
                  letterSpacing: '0.14em',
                  textTransform: 'uppercase',
                }}
              >
                {brand.subtitle}
              </span>
            </div>
          </>
        )}
        {isDrawer ? (
          <button
            ref={closeRef}
            type="button"
            aria-label="Close menu"
            onClick={onClose}
            style={iconButtonStyle}
          >
            <NavIcon name="close" color="var(--gc-charcoal)" size={20} />
          </button>
        ) : null}
      </div>

      <NavGroup
        label="Workspace"
        items={primaryItems}
        activeId={activeId}
        onNavigate={onNavigate}
      />
      {/* Setup / configuration items. Unlabelled: they used to sit under an
          "Insights" heading that stopped describing them once Reports moved into
          the Dashboard. A rule and the gap carry the separation instead. */}
      <div
        style={{
          height: 1,
          background: '#F0F2EE',
          margin: '18px 10px',
        }}
      />
      <NavGroup items={secondaryItems} activeId={activeId} onNavigate={onNavigate} />

      <div
        style={{
          marginTop: 'auto',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          padding: '12px 8px',
          borderTop: '1px solid #F0F2EE',
        }}
      >
        <div
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            background: 'var(--gc-green-dark)',
            color: 'var(--gc-white)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 12,
          }}
        >
          {user.initials}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', lineHeight: 1.2 }}>
          <span
            style={{
              fontFamily: 'var(--font-sans)',
              fontWeight: 600,
              fontSize: 12,
              color: 'var(--fg)',
            }}
          >
            {user.name}
          </span>
          <span style={{ fontFamily: 'var(--font-sans)', fontSize: 10, color: 'var(--gc-stone)' }}>
            {user.role}
          </span>
        </div>
      </div>
    </aside>
  );

  if (!isDrawer) return aside;

  return (
    <>
      <div
        aria-hidden="true"
        onClick={onClose}
        style={{
          position: 'fixed',
          inset: 0,
          zIndex: 999,
          background: 'rgba(28, 42, 34, 0.35)',
          opacity: open ? 1 : 0,
          pointerEvents: open ? 'auto' : 'none',
          transition: `opacity ${ms}ms var(--ease-out, ease-out)`,
        }}
      />
      {aside}
    </>
  );
}

const iconButtonStyle: CSSProperties = {
  marginLeft: 'auto',
  width: 40,
  height: 40,
  flexShrink: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: 'transparent',
  border: 'none',
  borderRadius: 10,
  cursor: 'pointer',
};

function NavGroup({
  label,
  items,
  activeId,
  onNavigate,
}: {
  /** Omit for an unlabelled group — the spacing alone separates it. */
  label?: string;
  items: SidebarItem[];
  activeId: string;
  onNavigate: (id: string) => void;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      {label ? (
        <div
          style={{
            fontFamily: 'var(--font-sans)',
            fontWeight: 600,
            fontSize: 10,
            letterSpacing: 'var(--track-xwide)',
            textTransform: 'uppercase',
            color: 'var(--gc-stone)',
            padding: '8px 10px 6px',
          }}
        >
          {label}
        </div>
      ) : null}
      {items.map((it) => {
        const isActive = activeId === it.id;
        return (
          <button
            key={it.id}
            type="button"
            onClick={() => onNavigate(it.id)}
            data-active={isActive ? 'true' : 'false'}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 10,
              padding: '10px 12px',
              background: isActive ? 'var(--gc-green-soft)' : 'transparent',
              border: 'none',
              borderRadius: 10,
              cursor: 'pointer',
              textAlign: 'left',
              fontFamily: 'var(--font-sans)',
              fontWeight: isActive ? 600 : 400,
              fontSize: 13,
              color: isActive ? 'var(--gc-green-dark)' : 'var(--gc-charcoal)',
              transition: 'background var(--dur-fast) var(--ease-out)',
              width: '100%',
            }}
          >
            <NavIcon name={it.icon} color={isActive ? 'var(--gc-green-dark)' : 'var(--gc-stone)'} />
            <span style={{ flex: 1 }}>{it.label}</span>
            {it.count != null ? (
              <span
                style={{
                  background: isActive ? 'var(--gc-green-dark)' : 'var(--gc-orange)',
                  // White on the orange is 1.9:1; ink on it is 7.7:1 (WCAG AA).
                  color: isActive ? 'var(--gc-white)' : 'var(--gc-ink)',
                  fontSize: 10,
                  fontWeight: 600,
                  padding: '2px 7px',
                  borderRadius: 'var(--radius-pill)',
                  minWidth: 18,
                  textAlign: 'center',
                }}
              >
                {it.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </div>
  );
}
